import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';

const comparisonRows = [
  { dimension: 'Speed', agent: 'Completes defined tasks in minutes to hours, 24/7, with consistent throughput.', traditional: 'Dependent on team size, working hours, and individual capacity. Bottlenecks at scale.' },
  { dimension: 'Cost', agent: 'Fixed subscription or per-use cost. Does not scale linearly with output volume.', traditional: 'Scales linearly with headcount. Includes salary, benefits, training, and management overhead.' },
  { dimension: 'Availability', agent: '24/7 operation with no sick leave, time-zone constraints, or fatigue.', traditional: 'Limited to working hours across time zones. Subject to leave, turnover, and capacity gaps.' },
  { dimension: 'Consistency', agent: 'Produces uniform outputs following defined rules, standards, and policies on every run.', traditional: 'Output quality varies by individual, experience level, workload, and day-to-day condition.' },
  { dimension: 'Scalability', agent: 'Scale throughput instantly without additional hiring, onboarding, or management overhead.', traditional: 'Scaling requires hiring, onboarding, training, and ongoing management investment.' },
];

const whenToUse = [
  {
    scenario: 'Use AI Agents When',
    points: [
      'The task is high-volume and repeatable with clear, defined outputs',
      'Work follows consistent rules, standards, or policies each time',
      'Speed and throughput matter more than creative judgment',
      'The domain is well-defined (reconciliation, architecture review, candidate screening)',
      'You need 24/7 operation without staffing costs',
    ],
    color: '#0071e3',
    bg: '#eff6ff',
  },
  {
    scenario: 'Use Human Expertise When',
    points: [
      'The situation requires novel judgment, emotional intelligence, or relationship management',
      'Decisions carry significant ethical, legal, or strategic weight',
      'The problem is ambiguous, unprecedented, or poorly defined',
      'Client trust and interpersonal dynamics are central to the outcome',
      'Complex negotiations, escalations, or high-stakes decisions are involved',
    ],
    color: '#047857',
    bg: '#f0fdf4',
  },
];

const agentRoles = [
  { role: 'Cloud Architect', replaces: 'Junior architecture documentation', augments: 'Senior architect review and design decisions', link: '/agents/cloud-architect' },
  { role: 'CPA Agent', replaces: 'Manual reconciliations and routine reporting', augments: 'CFO judgment, audit decisions, strategic finance', link: '/agents/cpa' },
  { role: 'Recruiter Agent', replaces: 'CV screening, shortlisting, outreach drafts', augments: 'Recruiter relationship management and final hiring decisions', link: '/agents/recruiter-agent' },
  { role: 'Interview Coach', replaces: 'Structured question prep and initial candidate scoring', augments: 'Hiring manager judgment and cultural fit assessment', link: '/agents/interview-agent' },
  { role: 'SDR Agent', replaces: 'Prospect research and initial outreach drafts', augments: 'Sales rep relationship development and deal closing', link: '/agents/sdr-agent' },
  { role: 'Data Engineer Agent', replaces: 'Boilerplate pipeline code and routine schema tasks', augments: 'Senior engineer architectural decisions and complex transformations', link: '/agents/data-engineering' },
];

const faqs = [
  {
    q: 'Can AI agents replace human workers?',
    a: 'Eagentix agents are designed to augment, not fully replace, human expertise. They handle high-volume, repetitive tasks so human experts focus on judgment, exceptions, and strategic decisions.',
  },
  {
    q: 'Are AI agents faster than human teams?',
    a: 'For well-defined repetitive tasks, AI agents are significantly faster. The Eagentix Cloud Architect Agent produces architecture documentation in 3 hours versus 24 hours manually.',
  },
  {
    q: 'When should I use AI agents instead of hiring?',
    a: 'AI agents are most effective for high-volume, repeatable work with clear outputs: documentation, reconciliations, screening, pipeline builds, reporting. Complex judgment, relationship management, and novel problem-solving still benefit from human expertise.',
  },
  {
    q: 'Do AI agents work 24/7?',
    a: 'Yes. Unlike human teams, Eagentix agents operate 24/7 without fatigue, sick leave, or time-zone constraints, ensuring continuous throughput on automated workflows.',
  },
  {
    q: 'How do AI agents compare to RPA (Robotic Process Automation)?',
    a: 'AI agents handle unstructured inputs, natural language, and complex reasoning that traditional RPA cannot. Eagentix agents understand context, apply domain knowledge, and adapt to variation — RPA requires rigid, structured inputs.',
  },
];

export default function AIAgentsVsTraditional() {
  const sectionLight = { padding: '80px 0', background: '#f8fafc' };
  const sectionWhite = { padding: '80px 0', background: 'white' };
  const sectionDark = { padding: '80px 0', background: '#0f172a' };
  const container = { maxWidth: '1100px', margin: '0 auto', padding: '0 24px' };
  const card = { borderRadius: '20px', border: '1px solid rgba(148,163,184,0.16)', background: 'white', padding: '28px 32px' };

  return (
    <>
      <SEO
        title="AI Agents vs Traditional Teams: What Changes? | Eagentix"
        description="A clear-eyed comparison of AI agents versus traditional human teams across speed, cost, availability, consistency, and scalability — plus when to use each."
        pathname="/compare/ai-agents-vs-traditional-teams"
      />

      {/* Hero */}
      <section style={{ ...sectionDark, paddingTop: '120px' }}>
        <div style={container}>
          <div style={{ maxWidth: '800px' }}>
            <span style={{ color: '#60a5fa', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.15em' }}>
              Comparison · AI Agents vs Teams
            </span>
            <h1 style={{ fontSize: '3rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', margin: '16px 0 24px' }}>
              AI Agents vs Traditional Teams: What Changes?
            </h1>
            <p style={{ fontSize: '1.2rem', color: '#cbd5e1', lineHeight: 1.75, marginBottom: '36px' }}>
              A clear-eyed look at what AI agents do better, what human teams do better, and how to design the right combination for your enterprise workflows.
            </p>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://app.eagentix.com/register"
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
              >
                Explore Agents
              </a>
              <a
                href="/#contact"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
              >
                Talk to Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            Side-by-Side Comparison
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Five dimensions where AI agents and traditional teams differ most in enterprise contexts.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
              <thead>
                <tr>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: 700, color: '#0f172a', background: '#e2e8f0', borderRadius: '12px 0 0 0', minWidth: '120px' }}>Dimension</th>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: 700, color: '#0071e3', background: '#dbeafe', minWidth: '280px' }}>AI Agent</th>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: 700, color: '#374151', background: '#e2e8f0', borderRadius: '0 12px 0 0', minWidth: '280px' }}>Traditional Team</th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc' }}>
                    <td style={{ padding: '16px 20px', fontWeight: 700, color: '#0f172a', borderBottom: '1px solid #e2e8f0' }}>{row.dimension}</td>
                    <td style={{ padding: '16px 20px', color: '#1e40af', lineHeight: 1.65, borderBottom: '1px solid #e2e8f0' }}>{row.agent}</td>
                    <td style={{ padding: '16px 20px', color: '#475569', lineHeight: 1.65, borderBottom: '1px solid #e2e8f0' }}>{row.traditional}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* When to use AI agents vs humans */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            When to Use AI Agents vs Human Expertise
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            The right answer depends on the nature of the work — not a binary choice between AI and people.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
            {whenToUse.map((item, i) => (
              <div key={i} style={{ ...card, borderTop: `4px solid ${item.color}` }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: item.color, marginBottom: '16px' }}>{item.scenario}</h3>
                <ul style={{ margin: 0, padding: '0 0 0 18px', color: '#475569', lineHeight: 1.8 }}>
                  {item.points.map((p, j) => <li key={j} style={{ marginBottom: '6px' }}>{p}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eagentix agents that replace or augment roles */}
      <section style={sectionLight}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '12px' }}>
            How Eagentix Agents Fit Alongside Your Team
          </h2>
          <p style={{ color: '#475569', fontSize: '1.05rem', lineHeight: 1.75, marginBottom: '40px', maxWidth: '680px' }}>
            Each Eagentix agent replaces specific repetitive tasks while augmenting — not replacing — the expert human judgment that drives outcomes.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {agentRoles.map((item, i) => (
              <div key={i} style={{ ...card, display: 'grid', gridTemplateColumns: '180px 1fr 1fr', gap: '24px', alignItems: 'start' }}>
                <div>
                  <Link to={item.link} style={{ color: '#0071e3', fontWeight: 700, fontSize: '1rem', textDecoration: 'none' }}>{item.role}</Link>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Replaces (automates)</div>
                  <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>{item.replaces}</p>
                </div>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '6px' }}>Augments (amplifies)</div>
                  <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.6, margin: 0 }}>{item.augments}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={sectionWhite}>
        <div style={container}>
          <h2 style={{ fontSize: '2rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '40px' }}>
            Frequently Asked Questions
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {faqs.map((faq, i) => (
              <div key={i} style={card}>
                <h3 style={{ color: '#0071e3', fontSize: '1.05rem', fontWeight: 700, margin: '0 0 12px 0' }}>{faq.q}</h3>
                <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.75, margin: 0 }}>{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={sectionDark}>
        <div style={{ ...container, textAlign: 'center' }}>
          <h2 style={{ fontSize: '2.2rem', fontWeight: 800, color: 'white', letterSpacing: '-0.03em', marginBottom: '16px' }}>
            Design the Right Human-AI Mix
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1.1rem', lineHeight: 1.75, marginBottom: '36px', maxWidth: '600px', margin: '0 auto 36px' }}>
            Eagentix helps enterprises identify which workflows to automate first and how to augment human expertise with purpose-built AI agents.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.eagentix.com/register"
              target="_blank"
              rel="noopener noreferrer"
              style={{ background: '#0071e3', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem' }}
            >
              Start Free
            </a>
            <a
              href="/#contact"
              style={{ background: 'rgba(255,255,255,0.1)', color: 'white', padding: '14px 28px', borderRadius: '12px', fontWeight: 700, textDecoration: 'none', fontSize: '1rem', border: '1px solid rgba(255,255,255,0.2)' }}
            >
              Book a Meeting
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
